import { HttpClient } from 'Mri.Web.Common';
import { action, observable, runInAction } from 'mobx';

export interface SharedPackage {
    name: string;
    /** 'package' = restored from the smartvu-shared feed; 'project' = built from this repository. */
    source?: string;
    repository?: string;
    /** The version the build actually restored, from the runtime deps file. */
    packageVersion?: string;
    /** The version smartvu-shared baked inside the dll, which can lag the restored package. */
    assemblyVersion?: string;
    versionMismatch?: boolean;
    loaded?: boolean;
    commit?: string;
    commitUrl?: string;
}

export interface WebBuild {
    repository?: string;
    commit?: string;
    shortCommit?: string;
    branch?: string;
    commitUrl?: string;
    pipelineId?: string;
    pipelineUrl?: string;
    builtAtUtc?: string;
    assemblyVersion?: string;
    informationalVersion?: string;
    stamped?: boolean;
    notStampedReason?: string;
}

export interface ImageInfo {
    tag?: string;
    tier?: string;
}

export interface RuntimeInfo {
    /** dev / qa / stg / prd — the deployment, not the ASP.NET environment name. */
    deployEnvironment?: string;
    /** ASPNETCORE_ENVIRONMENT: 'Staging' on dev, qa AND stg, so it cannot identify the deployment. */
    aspNetCoreEnvironment?: string;
    machineName?: string;
    processStartedAtUtc?: string;
    frameworkVersion?: string;
}

export interface BuildMetadata {
    web: WebBuild;
    /** MriSharedPackageVersion — the single bump point this build asked for (e.g. 0.1.0-alpha.9). */
    sharedPackageVersion?: string;
    sharedPackages: SharedPackage[];
    image: ImageInfo;
    runtime: RuntimeInfo;
}

export class SmartVuMetadataStore {
    public constructor(httpClient: HttpClient) {
        this.httpClient = httpClient;
    }

    @action
    public async loadAsync(): Promise<void> {
        this.loading = true;
        this.error = undefined;

        try {
            const response = await this.httpClient.get<BuildMetadata>('/api/v1/metadata/build');
            runInAction(() => {
                this.metadata = response;
            });
        } catch (e) {
            runInAction(() => {
                this.error = e instanceof Error ? e.message : 'Could not load build metadata.';
            });
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    }

    @observable public metadata?: BuildMetadata = undefined;
    @observable public loading = false;
    @observable public error?: string = undefined;

    private readonly httpClient: HttpClient;
}
